import { useEventSource } from "@/hooks/use-event-source";
import { useEffect, useState } from "react";
import { Markdown } from "../markdown/Markdown";

/**
 * ISOLATED / REMOVABLE FEATURE — see _docs/GEMINI_PROGRESS_STREAMING.md.
 *
 * Streams Gemini Deep Research "thinking" (research plan + reasoning) into the
 * "Generating Research..." view while the document is still generating.
 *
 * Self-gating: the backend SSE endpoint completes immediately with no events
 * when the feature flag is off or the research is not a Gemini interaction, so
 * this panel simply renders nothing in those cases. To remove the feature,
 * delete this file and its usage in `DocumentPage.tsx`.
 */

type ThinkingStreamEvent =
  | { type: "thought"; text: string }
  | { type: "status"; status: string }
  | { type: "completed" }
  | { type: "error"; message: string };

type ResearchThinkingPanelProps = {
  groupUuid: string | null;
  kitUuid: string | null;
  documentUuid: string;
};

export const ResearchThinkingPanel = ({
  groupUuid,
  kitUuid,
  documentUuid,
}: ResearchThinkingPanelProps) => {
  const [thoughts, setThoughts] = useState<string[]>([]);

  const { request, abort } = useEventSource<ThinkingStreamEvent>({
    method: "GET",
    basePath: `/brain/api/v1/groups/${groupUuid}/kits`,
  });

  useEffect(() => {
    if (!groupUuid || !kitUuid) {
      return;
    }

    // Connect on mount, abort on cleanup. No ref-guard: under React
    // StrictMode the mount→cleanup→remount cycle must be allowed to
    // re-establish the connection (a guard would abort the first
    // connection and then skip the reconnect, leaving nothing live).
    request({
      path: `/${kitUuid}/documents/${documentUuid}/research-thinking`,
      onMessage: (event) => {
        if (event.type === "thought") {
          setThoughts((prev) => [...prev, event.text]);
        }
      },
      onError: () => {
        // Best-effort, non-blocking: the report still arrives via polling.
      },
      // Aborting rejects the underlying fetch; swallow it so it doesn't
      // surface as an unhandled rejection during StrictMode cleanup.
    }).catch(() => {});

    return () => {
      abort();
    };
  }, [groupUuid, kitUuid, documentUuid]);

  if (thoughts.length === 0) {
    return null;
  }

  return (
    <div className="border-muted mt-2 flex flex-col gap-3 border-l-2 pl-4">
      <div className="text-muted-foreground text-sm font-medium">
        Research progress
      </div>
      {thoughts.map((thought, index) => (
        <Markdown
          key={index}
          className="text-muted-foreground animate-in fade-in text-sm"
        >
          {thought}
        </Markdown>
      ))}
    </div>
  );
};
